<script setup lang="ts">
import { computed } from "vue";
import type { Product } from "@shopware-pwa/types";
import { Accordion } from "~/components/clarity-ui/Accordion";
import { AccordionDetails } from "~/components/clarity-ui/Accordion/AccordionDetails";
import { AccordionSummary } from "~/components/clarity-ui/Accordion/AccordionSummary";
import useNavigationTracking from "~/composables/tracking/useNavigationTracking";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import DownloadLinkComponent from "~/templates/components/cms/DownloadLinkComponent.vue";
import { mapNodes } from "~/utils/helper/magnolia";

export interface ProductTechnicalSpecProps {
  content?: {
    downloadHeadline?: string;
    downloadOpen?: boolean;
    downloadVisible?: boolean;
    hiddenSpecs?: string[];
    openSpecs?: string[];
    richText?: string;
    visibleSpecs?: string[];
  };
  downloads?: object;
  product?: Product;
}

const { content, product } = defineProps<ProductTechnicalSpecProps>();

const VISIBLE_SPECS_DEFAULT = [
  "brita_product_data_safety_instructions",
  "brita_product_data_details_and_dimensions",
  "brita_product_data_materials_and_maintenance",
  "brita_product_data_compatibility",
  "brita_product_data_technical_details",
  "brita_product_data_installation_requirements",
  "brita_product_data_instructions",
  "brita_product_data_nutritional_values",
  "brita_product_data_ingredients",
  "brita_product_data_preparation",
  "brita_product_data_varieties_overview",
  "subscription_technical_specs",
];

const { trackTechnicalSpecs } = useNavigationTracking();

// prefilter specs
const specsWithContent = computed(() => {
  const hiddenSpecs = content?.hiddenSpecs ?? [];

  // check, if there is actual content for the key in product
  return VISIBLE_SPECS_DEFAULT?.filter(
    (spec) => !hiddenSpecs.includes(spec) && product?.customFields?.[spec]?.length > 0,
  );
});

const hasContent = computed(() => specsWithContent.value.length > 0 || content?.downloadVisible);
</script>

<template>
  <div v-if="hasContent">
    <Accordion divider>
      <template v-for="spec in specsWithContent" :key="spec">
        <AccordionDetails
          :open="content?.openSpecs?.includes(spec)"
          @toggle="trackTechnicalSpecs(spec, product?.translated?.name)"
        >
          <AccordionSummary>
            <CommonLabel :path="`BWF/shop/product/specs/${spec}`" />
          </AccordionSummary>
          <div class="prose prose-black" v-html="product.customFields[spec]" />
        </AccordionDetails>
      </template>
      <AccordionDetails v-if="content?.downloadVisible" :open="!!content?.downloadOpen">
        <AccordionSummary>
          <template v-if="content?.downloadHeadline">
            {{ content?.downloadHeadline }}
          </template>
          <CommonLabel v-else path="BWF/shop/product/specs/cmsDownloadHeadline" />
        </AccordionSummary>
        <div class="grid items-start gap-4">
          <div v-if="content?.richText" class="prose" v-html="content?.richText" />
          <DownloadLinkComponent
            v-for="download in mapNodes(content?.downloads)"
            :key="download['@id']"
            :download="download?.download"
          />
        </div>
      </AccordionDetails>
    </Accordion>
  </div>
</template>
